import _ from 'lodash'
import fetch from 'node-fetch' // this is for tests; no-op for built website
import { useEffect, useState } from 'react'
import { numberOfDevsOptions } from './components/form/GetStartedForm'

/**
 * GCP load balancers set a header to detect client region.
 * Good reference pic for country codes: https://www.heliosdesign.com/glossary/what-are-iso-country-codes.html
 *
 * The region value is undefined before request is complete, then a string. On a request error,
 * or if GCP load balancer doesn't return the expected header, value is set to null.
 */
export function useRegion() {
  const [region, setRegion] = useState<string | null | undefined>(undefined)
  useEffect(() => {
    void fetch(`https://geo.swarmia.com/region/`)
      .then(response => {
        const region = response.headers.get('x-client-region')
        setRegion(region || null)
      })
      .catch(() => {
        setRegion(null)
      })
  }, [])
  return region
}

function isEurRegion(region: string) {
  return [
    'AO',
    'BF',
    'BI',
    'BJ',
    'BW',
    'CF',
    'CG',
    'CI',
    'CM',
    'CV',
    'DJ',
    'DZ',
    'EG',
    'EH',
    'ER',
    'ET',
    'GA',
    'GH',
    'GM',
    'GN',
    'GQ',
    'GW',
    'KE',
    'KM',
    'LR',
    'LS',
    'LY',
    'MA',
    'MG',
    'ML',
    'MR',
    'MU',
    'MW',
    'MZ',
    'NA',
    'NE',
    'NG',
    'RE',
    'RW',
    'SC',
    'SD',
    'SL',
    'SN',
    'SO',
    'ST',
    'SZ',
    'TD',
    'TG',
    'TN',
    'TZ',
    'UG',
    'YT',
    'ZA',
    'ZM',
    'ZR',
    'ZW',
    'AE',
    'AF',
    'AM',
    'AZ',
    'BD',
    'BH',
    'BN',
    'BT',
    'CC',
    'CN',
    'CX',
    'CY',
    'GE',
    'ID',
    'IL',
    'IN',
    'IQ',
    'IR',
    'JO',
    'JP',
    'KG',
    'KH',
    'KP',
    'KR',
    'KW',
    'KZ',
    'LA',
    'LB',
    'LK',
    'MM',
    'MN',
    'MV',
    'MY',
    'NP',
    'OM',
    'PH',
    'PK',
    'QA',
    'RU',
    'SA',
    'SG',
    'SY',
    'TH',
    'TJ',
    'TM',
    'TR',
    'TW',
    'UZ',
    'VN',
    'YE',
    'AD',
    'AL',
    'AT',
    'BA',
    'BE',
    'BG',
    'BY',
    'CH',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FO',
    'FR',
    'GB',
    'GI',
    'GR',
    'HR',
    'HU',
    'IE',
    'IS',
    'IT',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'MD',
    'MK',
    'MT',
    'NL',
    'NO',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SJ',
    'SK',
    'SM',
    'UA',
    'VA',
    'IM',
    'CH',
    'XK',
    'ME',
    'RS',
  ].includes(region)
}

export function useCurrency() {
  const region = useRegion()
  if (_.isNil(region)) {
    return region
  }
  return isEurRegion(region) ? 'EUR' : 'USD'
}

function isUsSalesRegion(region: string) {
  return [
    'AG',
    'AI',
    'AN',
    'AR',
    'AW',
    'BB',
    'BM',
    'BO',
    'BR',
    'BS',
    'BZ',
    'CA',
    'CL',
    'CO',
    'CR',
    'CU',
    'DM',
    'DO',
    'EC',
    'FK',
    'GD',
    'GF',
    'GL',
    'GP',
    'GT',
    'GY',
    'HN',
    'HT',
    'JM',
    'KN',
    'KY',
    'LC',
    'MQ',
    'MS',
    'MX',
    'NI',
    'PA',
    'PE',
    'PM',
    'PR',
    'PY',
    'SR',
    'SV',
    'TC',
    'TT',
    'US',
    'UY',
    'VC',
    'VE',
    'VG',
    'VI',
    'AS',
    'AU',
    'CK',
    'FJ',
    'FM',
    'GU',
    'KI',
    'MH',
    'MP',
    'NC',
    'NF',
    'NR',
    'NU',
    'NZ',
    'PF',
    'PG',
    'PN',
    'PW',
    'SB',
    'TK',
    'TO',
    'TV',
    'VU',
    'WF',
    'WS',
  ].includes(region)
}

// Which region's office should we book the demo meeting for. If the request for sales_region.json comes from these countries,
// the request is redirected to the sales_region.us.json file, which defines the sales region as being US.
// Shortly: Americas, Australia and New Zealand go to the US office, others to Europe.
export type SalesRegion = 'EU' | 'US-East' | 'US-West' | 'US-Large'
export function useSalesRegion(
  numberOfDevs: string,
): SalesRegion | null | undefined {
  const region = useRegion()
  if (_.isNil(region)) {
    return region
  }
  return isUsSalesRegion(region) ? getUsSalesRegion(numberOfDevs) : 'EU'
}

function isUsWestTimezone(tz: string): boolean {
  return [
    'America/Edmonton',
    'America/Denver',
    'America/Boise',
    'America/Vancouver',
    'America/Phoenix',
    'America/Los_Angeles',
    'Pacific/Honolulu',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Perth',
    'Pacific/Guadalcanal',
    'Pacific/Auckland',
  ].includes(tz)
}

// EU vs US is decided based on geo location (IP address), but the US east/west split is decided based on browser timezone
function getUsSalesRegion(numberOfDevs: string) {
  if (
    numberOfDevs === numberOfDevsOptions[4].value ||
    numberOfDevs === numberOfDevsOptions[5].value ||
    numberOfDevs === numberOfDevsOptions[6].value ||
    numberOfDevs === numberOfDevsOptions[7].value
  ) {
    return 'US-Large'
  }
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return isUsWestTimezone(tz) ? 'US-West' : 'US-East'
}

export function getCalendarBookingLink(
  salesRegion: SalesRegion | null | undefined,
): string {
  switch (salesRegion) {
    case 'EU':
      return 'https://meetings.hubspot.com/swarmia/emea'
    case 'US-Large':
      return 'https://link.swarmia.com/meetings/swarmia/us-enterprise'
    case 'US-West':
      return 'https://meetings.hubspot.com/swarmia/america'
    // Default to US-East as most of the leads come from there
    default:
    case 'US-East':
      return 'https://link.swarmia.com/meetings/swarmia/us-east'
  }
}
